import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LoadingSpinner } from "@/components/ui/spinner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { lazy, Suspense } from "react";
import ScrollToTop from "./components/ScrollToTop";
import EventAnnouncementBar from "./components/EventAnnouncementBar";
import RouteSeo from "./components/RouteSeo";
import LanguageWrapper from "./components/LanguageWrapper";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

const About = lazy(() => import("./pages/About"));
const Logistics = lazy(() => import("./pages/Logistics"));
const Buildings = lazy(() => import("./pages/Buildings"));
const Manufacturing = lazy(() => import("./pages/Manufacturing"));
const Careers = lazy(() => import("./pages/Careers"));
const SoftwareDevRole = lazy(() => import("./pages/Roles/SoftwareDevRole"));
const BizDevRole = lazy(() => import("./pages/Roles/BizDevRole"));
const EnergySolutionsRole = lazy(() => import("./pages/Roles/EnergySolutionsRole"));
const Contact = lazy(() => import("./pages/Contact"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Sim = lazy(() => import("./pages/Solutions/Sim"));
const Ops = lazy(() => import("./pages/Solutions/Ops"));
const EventRegistration = lazy(() => import("./pages/EventRegistration"));
const EventConfirmed = lazy(() => import("./pages/EventConfirmed"));
const EventReceived = lazy(() => import("./pages/EventReceived"));
const EventCancelled = lazy(() => import("./pages/EventCancelled"));
const EventDeclined = lazy(() => import("./pages/EventDeclined"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));

const EventLegacyRedirect = () => {
  const { slug, "*": splat } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/${getPreferredLang()}/events/${slug}${splat ? `/${splat}` : ""}${search}`} replace />;
};

const queryClient = new QueryClient();

const getPreferredLang = () => {
  const saved = localStorage.getItem("lang");
  if (saved === "en" || saved === "nl") return saved;
  return navigator.language.startsWith("nl") ? "nl" : "en";
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <RouteSeo />
        <ScrollToTop />
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center">
            <LoadingSpinner size="lg" />
          </div>
        }>
          <Routes>
            {/* Root: redirect to preferred language */}
            <Route path="/" element={<Navigate to={`/${getPreferredLang()}`} replace />} />

            {/* Legacy paths: redirect to language-prefixed equivalents */}
            <Route path="/about" element={<Navigate to={`/${getPreferredLang()}/about`} replace />} />
            <Route path="/industries/logistics" element={<Navigate to={`/${getPreferredLang()}/industries/logistics`} replace />} />
            <Route path="/industries/buildings" element={<Navigate to={`/${getPreferredLang()}/industries/buildings`} replace />} />
            <Route path="/industries/manufacturing" element={<Navigate to={`/${getPreferredLang()}/industries/manufacturing`} replace />} />
            <Route path="/careers" element={<Navigate to={`/${getPreferredLang()}/careers`} replace />} />
            <Route path="/contact" element={<Navigate to={`/${getPreferredLang()}/contact`} replace />} />
            <Route path="/privacy" element={<Navigate to={`/${getPreferredLang()}/privacy`} replace />} />
            <Route path="/terms" element={<Navigate to={`/${getPreferredLang()}/terms`} replace />} />
            <Route path="/solutions/sim" element={<Navigate to={`/${getPreferredLang()}/solutions/sim`} replace />} />
            <Route path="/solutions/ops" element={<Navigate to={`/${getPreferredLang()}/solutions/ops`} replace />} />
            <Route path="/events/:slug/*" element={<EventLegacyRedirect />} />

            {/* Unsubscribe — token-based, no language prefix */}
            <Route path="/unsubscribe" element={<Unsubscribe />} />

            {/* Language-prefixed routes */}
            <Route path="/:lang" element={<LanguageWrapper />}>
              <Route index element={<Index />} />
              <Route path="about" element={<About />} />
              <Route path="industries/logistics" element={<Logistics />} />
              <Route path="industries/buildings" element={<Buildings />} />
              <Route path="industries/manufacturing" element={<Manufacturing />} />
              <Route path="solutions/sim" element={<Sim />} />
              <Route path="solutions/ops" element={<Ops />} />
              <Route path="careers" element={<Careers />} />
              <Route path="careers/business-developer-fleet-electrification" element={<BizDevRole />} />
              <Route path="careers/energy-solutions-engineer" element={<EnergySolutionsRole />} />
              <Route path="careers/software-engineer" element={<SoftwareDevRole />} />
              <Route path="contact" element={<Contact />} />
              <Route path="privacy" element={<Privacy />} />
              <Route path="terms" element={<Terms />} />
              <Route path="events/:slug/" element={<EventRegistration />} />
              <Route path="events/:slug/apply" element={<EventRegistration />} />
              <Route path="events/:slug/preview" element={<EventRegistration />} />
              <Route path="events/:slug/confirmed" element={<EventConfirmed />} />
              <Route path="events/:slug/received" element={<EventReceived />} />
              <Route path="events/:slug/cancelled" element={<EventCancelled />} />
              <Route path="events/:slug/declined" element={<EventDeclined />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <EventAnnouncementBar />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
